"use client";

import { type HTMLAttributes, useRef, type FC, type Ref } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses, limit } from "@huub-web/utils";

import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { fillStyle, trackStyle } from "./ProgressBar.css";

export type ProgressBarProps = HTMLAttributes<HTMLDivElement> &
  RecipeVariants<typeof trackStyle> & {
    progress?: number;
    align?: "top" | "bottom";
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A progress bar component that can be used to show the progress of a task.
 */
export const ProgressBar: FC<ProgressBarProps> = ({
  variant = "default",
  position = "default",
  align = "top",
  progress = 0,
  color,
  className,
  ref,
  ...props
}) => {
  const fillRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!fillRef.current || variant === "infinite") {
      return;
    }
    fillRef.current.style.width = `${limit(progress, 0, 100)}%`;
  }, [progress, variant]);

  return (
    <div
      ref={ref}
      className={mergeClasses(
        trackStyle({ variant, position, color, align }),
        className,
      )}
      {...props}
    >
      <div
        ref={fillRef}
        className={mergeClasses(fillStyle({ variant, position, color }))}
      />
    </div>
  );
};
